import React, { useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { fmtDop } from "../helpers";
// products.price and compare_at_price are already in DOP — render via fmtDop.

/**
 * OffersManager — Admin tab "Ofertas".
 * Source of truth: public.products (price + compare_at_price).
 * Applying a % discount:
 *   - sets compare_at_price = original price (if not already set)
 *   - sets price = round( compare_at_price * (1 - %/100) )
 * Clearing an offer:
 *   - restores price = compare_at_price, sets compare_at_price = null
 * Realtime on the products table keeps the home Hero, the "Ofertas Permanentes"
 * list, and the product detail page in sync automatically.
 */

type Row = {
  id: string;
  name: string;
  brand: string | null;
  sku: string | null;
  price: number;
  compare_at_price: number | null;
  image_url: string | null;
  is_active: boolean | null;
};

type Props = { lang: "es" | "en"; toast?: (msg: string, type?: string) => void };

const input: React.CSSProperties = {
  padding: "8px 10px", borderRadius: 8, border: "1px solid var(--bd2)",
  background: "var(--bg2)", color: "var(--tx)", fontSize: 13, width: "100%",
};

const btn = (kind: "primary" | "ghost" | "danger" = "primary"): React.CSSProperties => ({
  padding: "8px 12px", borderRadius: 8, fontSize: 12, fontWeight: 700,
  cursor: "pointer",
  border: kind === "ghost" ? "1px solid var(--bd2)" : "none",
  background: kind === "primary" ? "var(--ac)" : kind === "danger" ? "#dc2626" : "transparent",
  color: kind === "ghost" ? "var(--tx)" : "#fff",
});

export function OffersManager({ lang, toast }: Props) {
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [onlyOnSale, setOnlyOnSale] = useState(false);
  const [pct, setPct] = useState<Record<string, string>>({});
  const [savingId, setSavingId] = useState<string | null>(null);

  const fetchRows = async () => {
    const { data, error } = await supabase
      .from("products")
      .select("id,name,brand,sku,price,compare_at_price,image_url,is_active")
      .eq("is_active", true)
      .order("name");
    if (!error && data) setRows(data as any);
    setLoading(false);
  };

  useEffect(() => {
    fetchRows();
    const ch = supabase
      .channel("offers_manager_products")
      .on("postgres_changes", { event: "*", schema: "public", table: "products" }, () => fetchRows())
      .subscribe();
    return () => { supabase.removeChannel(ch); };
  }, []);

  const T = {
    title: lang === "es" ? "Ofertas" : "Offers",
    hint: lang === "es"
      ? "Aplica un % de descuento o un precio promocional. Se sincroniza al instante con el Hero, la página del producto y la lista de Ofertas Permanentes."
      : "Apply a discount % or a promo price. Syncs instantly across the Hero, product page and Permanent Offers list.",
    search: lang === "es" ? "Buscar por nombre, marca o SKU…" : "Search by name, brand or SKU…",
    onlySale: lang === "es" ? "Solo en oferta" : "On sale only",
    product: lang === "es" ? "Producto" : "Product",
    current: lang === "es" ? "Precio actual" : "Current price",
    original: lang === "es" ? "Precio original" : "Original price",
    pct: lang === "es" ? "% descuento" : "Discount %",
    apply: lang === "es" ? "Aplicar" : "Apply",
    clear: lang === "es" ? "Quitar oferta" : "Clear offer",
    none: lang === "es" ? "No hay productos." : "No products.",
    total: lang === "es" ? "productos" : "products",
    onSale: lang === "es" ? "en oferta" : "on sale",
  };

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return rows.filter(r => {
      if (onlyOnSale && !(r.compare_at_price && r.compare_at_price > r.price)) return false;
      if (!needle) return true;
      return [r.name, r.brand, r.sku]
        .filter(Boolean)
        .some(v => String(v).toLowerCase().includes(needle));
    });
  }, [rows, q, onlyOnSale]);

  const onSaleCount = rows.filter(r => r.compare_at_price && r.compare_at_price > r.price).length;

  const applyDiscount = async (r: Row) => {
    const raw = pct[r.id];
    const p = Number(raw);
    if (!raw || Number.isNaN(p) || p <= 0 || p >= 100) {
      toast?.(lang === "es" ? "Ingresa un % entre 1 y 99" : "Enter a % between 1 and 99", "error");
      return;
    }
    const original = r.compare_at_price && r.compare_at_price > r.price ? r.compare_at_price : r.price;
    const newPrice = Number((original * (1 - p / 100)).toFixed(2));
    setSavingId(r.id);
    const { error } = await supabase
      .from("products")
      .update({ price: newPrice, compare_at_price: original })
      .eq("id", r.id);
    setSavingId(null);
    if (error) toast?.(lang === "es" ? "Error al guardar" : "Save failed", "error");
    else {
      setPct(prev => ({ ...prev, [r.id]: "" }));
      toast?.(lang === "es" ? "Oferta aplicada" : "Offer applied", "success");
    }
  };

  const clearOffer = async (r: Row) => {
    if (!r.compare_at_price || r.compare_at_price <= r.price) return;
    setSavingId(r.id);
    const { error } = await supabase
      .from("products")
      .update({ price: r.compare_at_price, compare_at_price: null })
      .eq("id", r.id);
    setSavingId(null);
    if (error) toast?.(lang === "es" ? "Error al guardar" : "Save failed", "error");
    else toast?.(lang === "es" ? "Oferta removida" : "Offer cleared", "success");
  };

  return (
    <div style={{ padding: 4 }}>
      <div style={{ marginBottom: 12 }}>
        <h3 style={{ margin: 0, color: "var(--tx)" }}>{T.title}</h3>
        <p style={{ margin: "4px 0 0", color: "var(--tx3)", fontSize: 12 }}>{T.hint}</p>
      </div>

      <div style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 12, flexWrap: "wrap" }}>
        <div style={{ flex: 1, minWidth: 220 }}>
          <input style={input} value={q} onChange={e => setQ(e.target.value)} placeholder={T.search} />
        </div>
        <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "var(--tx)", cursor: "pointer" }}>
          <input type="checkbox" checked={onlyOnSale} onChange={e => setOnlyOnSale(e.target.checked)} />
          {T.onlySale}
        </label>
        <div style={{ fontSize: 11, color: "var(--tx3)" }}>
          {rows.length} {T.total} · {onSaleCount} {T.onSale}
        </div>
      </div>

      {loading ? (
        <div style={{ padding: 20, color: "var(--tx3)" }}>…</div>
      ) : filtered.length === 0 ? (
        <div style={{ padding: 20, color: "var(--tx3)" }}>{T.none}</div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {filtered.map(r => {
            const hasOffer = !!(r.compare_at_price && r.compare_at_price > r.price);
            const discount = hasOffer
              ? Math.round((1 - r.price / (r.compare_at_price as number)) * 100)
              : 0;
            return (
              <div
                key={r.id}
                style={{
                  display: "grid",
                  gridTemplateColumns: "48px 1fr auto auto auto",
                  gap: 12, alignItems: "center",
                  background: "var(--bg2)", border: "1px solid var(--bd2)",
                  borderRadius: 10, padding: 10,
                }}
              >
                <div style={{ width: 48, height: 48, borderRadius: 8, background: "var(--bg)", overflow: "hidden", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  {r.image_url ? (
                    <img src={r.image_url} alt="" style={{ width: "100%", height: "100%", objectFit: "contain" }} />
                  ) : <span style={{ fontSize: 18 }}>📦</span>}
                </div>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: "var(--tx)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {r.name}
                  </div>
                  <div style={{ fontSize: 11, color: "var(--tx3)" }}>
                    {r.brand || "—"}{r.sku ? ` · SKU ${r.sku}` : ""}
                  </div>
                </div>
                <div style={{ textAlign: "right", minWidth: 120 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: hasOffer ? "#dc2626" : "var(--tx)" }}>
                    {fmtDop(r.price)}
                  </div>
                  {hasOffer && (
                    <div style={{ fontSize: 11, color: "var(--tx3)", textDecoration: "line-through" }}>
                      {fmtDop(r.compare_at_price as number)} · -{discount}%
                    </div>
                  )}
                </div>
                <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                  <input
                    type="number" min={1} max={99}
                    placeholder={T.pct}
                    value={pct[r.id] ?? ""}
                    onChange={e => setPct(prev => ({ ...prev, [r.id]: e.target.value }))}
                    style={{ ...input, width: 80 }}
                  />
                  <button
                    style={btn("primary")}
                    disabled={savingId === r.id}
                    onClick={() => applyDiscount(r)}
                  >
                    {T.apply}
                  </button>
                </div>
                <button
                  style={{ ...btn("ghost"), opacity: hasOffer ? 1 : 0.4 }}
                  disabled={!hasOffer || savingId === r.id}
                  onClick={() => clearOffer(r)}
                >
                  {T.clear}
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default OffersManager;